import React, {useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import _ from "lodash";

export const ColorCollectionMiniGrid = ({ colors, intensityGroups, intensityGroupsExtraCls, gridRef }: {
  colors: {grid_position_row: number, grid_position_col: number, intensity: string}[],
  intensityGroups?: string[][],
  intensityGroupsExtraCls?: Record<string, string>,
  gridRef: React.RefObject<HTMLDivElement>,
}) => {

  const containerRef = useRef<HTMLDivElement>(null);

  const [area, setArea]
    = useState<{ top: number, left: number, width: number, height: number } | undefined>(undefined);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const updateArea = () => {
      if (!gridRef.current || !containerRef.current) return;

      const {
        scrollTop, scrollLeft,
        clientHeight, clientWidth,
        scrollHeight, scrollWidth,
      } = gridRef.current;

      const { width: miniGridWidth, height: miniGridHeight } = containerRef.current.getBoundingClientRect();

      const scaleX = miniGridWidth / scrollWidth;
      const scaleY = miniGridHeight / scrollHeight;

      const top = scrollTop * scaleY;
      const left = scrollLeft * scaleX;
      const width = clientWidth * scaleX;
      const height = clientHeight * scaleY;

      setArea({ top, left, width, height });

      // Show the minimap and reset the timeout
      setVisible(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setVisible(false), 1000);
    };

    const gridElement = gridRef.current;
    if (!!gridElement)
      gridElement.addEventListener("scroll", updateArea);

    return () => {
      if (!!gridElement)
        gridElement.removeEventListener("scroll", updateArea);
      clearTimeout(timeoutId);
    };
  }, [gridRef.current, containerRef.current]);

  if (!colors.length)
    return null;

  return ReactDOM.createPortal(
    <div
      ref={containerRef}
      className={`fixed bottom-2 right-2 p-1.5 bg-yellow-100 z-20 flex flex-row gap-2 items-center ${visible ? "" : "hidden"}`}
    >
      {(intensityGroups ?? [[""]]).map((labels, idx) => (
        <div key={`ig-${idx}`} className="flex flex-col gap-0.5">
          {labels.map((t) => {
            const groupColors = colors.filter((c) => !!t ? c.intensity === t : true);
            if (groupColors.length === 0)
              return null;
            const rows = _.groupBy(groupColors, (c) => c.grid_position_row);
            const maxCol = groupColors.reduce((prev, curr) => Math.max(prev, curr.grid_position_col), -1);
            return (
              <div key={t} className={`flex flex-col gap-0.5 ${intensityGroupsExtraCls?.[t] ?? ""}`}>
                {Object.entries(rows).map(([r, items]) => (
                  <div key={r} className="w-full flex gap-0.5">
                    {
                      Array.from({ length: maxCol + 1 }).map((_, idx) => {
                        const target = items.find((i) => i.grid_position_col === idx);
                        return (
                          <div
                            key={idx}
                            className={`w-2.5 ${!!target ? "bg-yellow-600" : "bg-transparent"}`}
                            style={{ aspectRatio: "16 / 9" }}
                          />
                        )
                      })
                    }
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
      {!!area && (
        <div
          className="absolute bg-green-500 z-30 bg-opacity-30"
          style={area}
        />
      )}
    </div>,
    document.body
  );
};